import type { SyncProvider } from './syncProvider.js';
import { GoogleDriveSyncProvider } from './googleDriveSyncProvider.js';
import { syncState } from './syncState.js';
import {
  syncNow as engineSyncNow,
  cleanupModuleFromDrive as engineCleanup,
  markModuleDeletedOnDrive as engineMarkDeleted,
} from './syncEngine.js';
import type { SyncResult } from './types.js';
import { maybeWriteCheckpoint } from './checkpoints.js';
import { isGoogleSyncConfigured } from '../config.js';

/**
 * Process-wide singleton. Mirrors Kotlin SyncManager — holds the active
 * provider, persists which provider was last selected, and silently
 * rehydrates auth on boot if previously consented.
 */

const PREFS_KEY = 'shell.syncProvider';
type ProviderName = 'google_drive' | null;

class SyncManager {
  private provider: SyncProvider | null = null;
  /**
   * Cache of the first initFromStorage() so concurrent callers share one
   * silent-resume network round-trip. Each module iframe has its own JS
   * context (and its own SyncManager singleton), so any bridge method that
   * touches Drive must ensure init has completed — see F1-a room + F2
   * share preflights — otherwise a fresh iframe answers "not connected"
   * even though the launcher iframe is signed in.
   */
  private initPromise: Promise<void> | null = null;

  /** Bootstrap: rehydrate provider preference + attempt silent auth. */
  initFromStorage(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    const saved = localStorage.getItem(PREFS_KEY) as ProviderName;
    if (!saved) {
      syncState.setNotConfigured();
      return;
    }
    if (saved === 'google_drive') {
      if (!isGoogleSyncConfigured()) {
        console.warn('[syncManager] Google saved as provider but VITE_GOOGLE_CLIENT_ID is empty');
        syncState.setNotConfigured();
        return;
      }
      const p = new GoogleDriveSyncProvider();
      const resumed = await p.trySilentResume();
      if (resumed) {
        this.provider = p;
        syncState.setIdle();
      } else {
        // Keep preference; user just needs to re-consent when they click Connect.
        syncState.setNotConfigured();
      }
    }
  }

  getActiveProviderName(): ProviderName {
    return (this.provider?.getProviderName() as ProviderName) ?? null;
  }

  getAccountEmail(): string | null { return this.provider?.getAccountEmail() ?? null; }
  isReady(): boolean { return this.provider !== null && this.provider.isAuthenticated(); }
  get(): SyncProvider | null { return this.provider; }

  /**
   * Switch to / connect a provider. For google_drive this triggers the OAuth
   * flow (popup). Returns true on success.
   */
  async selectProvider(name: 'google_drive'): Promise<boolean> {
    if (name === 'google_drive') {
      if (!isGoogleSyncConfigured()) {
        syncState.markError('Google Drive not configured (VITE_GOOGLE_CLIENT_ID missing)');
        return false;
      }
      const p = new GoogleDriveSyncProvider();
      const ok = await p.authenticate();
      if (!ok) {
        syncState.markError('Sign-in failed or was cancelled');
        return false;
      }
      this.provider = p;
      localStorage.setItem(PREFS_KEY, 'google_drive');
      syncState.setIdle();
      return true;
    }
    return false;
  }

  async disconnect(): Promise<void> {
    if (this.provider) await this.provider.signOut();
    this.provider = null;
    localStorage.removeItem(PREFS_KEY);
    syncState.setNotConfigured();
  }

  async syncNow(): Promise<SyncResult> {
    const result = await engineSyncNow(this.provider);
    // Additive history layer — write a rolling checkpoint when the sync changed
    // something. Never affects reconciliation; failures don't fail the sync.
    void maybeWriteCheckpoint(this.provider, result);
    return result;
  }

  /**
   * Remove a module's Drive snapshots — for SYNC-OFF toggles only. A missing
   * file means "not synced"; it no longer deletes anything on other devices.
   */
  async cleanupModuleFromDrive(id: string, deleteData: boolean, deleteCode: boolean): Promise<boolean> {
    return engineCleanup(this.provider, id, deleteData, deleteCode);
  }

  /**
   * Real module DELETION: writes a {deleted:true} tombstone over the code
   * snapshot (the explicit signal other devices act on) + drops the data one.
   */
  async markModuleDeletedOnDrive(id: string, tombstoneCode: boolean, deleteData: boolean): Promise<boolean> {
    return engineMarkDeleted(this.provider, id, tombstoneCode, deleteData);
  }

  /**
   * Selftest — list root, write+read+delete a probe file. Used by the
   * Settings UI to validate the round-trip is healthy.
   */
  async selfTest(): Promise<{ ok: boolean; message: string }> {
    if (!this.provider) return { ok: false, message: 'No provider selected' };
    if (!this.provider.isAuthenticated()) return { ok: false, message: 'Not authenticated' };
    try {
      const probe = `shell/selftest-${Date.now()}.txt`;
      const payload = `shell selftest ${new Date().toISOString()}`;
      const wrote = await this.provider.writeFile(probe, payload);
      if (!wrote) return { ok: false, message: 'writeFile failed' };
      const read = await this.provider.readFile(probe);
      if (read !== payload) return { ok: false, message: `readback mismatch: ${read?.slice(0, 60)}…` };
      const deleted = await this.provider.deleteFile(probe);
      if (!deleted) return { ok: false, message: 'deleteFile failed' };
      return { ok: true, message: 'Drive read/write/delete OK' };
    } catch (e) {
      return { ok: false, message: (e as Error).message };
    }
  }
}

export const syncManager = new SyncManager();
